import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  buildFeedFilters,
  dedupeEvents,
  filterFeed,
  kindsForPostTypes,
  nextUntil,
  sortEvents,
  type PostType,
} from "@/nostr/api/feed"
import type { NostrEvent } from "@/nostr/types"
import { useNostr } from "@/state/NostrContext"
import { useSettings } from "@/state/SettingsContext"
import { uniqueRelayUrls } from "@/utils/url"

export interface TimelineOptions {
  relays: string[]
  authors?: string[]
  kinds?: number[]
  search?: string
  limit?: number
  enabled?: boolean
  /** Lower bound on `created_at`, applied by the relay (time range filter). */
  since?: number
  /** Upper bound on `created_at`, e.g. the end of a custom range. */
  until?: number
  /** Overrides the user setting when provided. */
  includeReplies?: boolean
  /**
   * Post types to keep. The subscription narrows its `kinds` to the selection
   * and the reply/note split is applied client side (relays cannot express it).
   */
  postTypes?: readonly PostType[]
}

export interface TimelineResult {
  events: NostrEvent[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  eose: boolean
  closeReasons: string[]
  loadMore: () => void
  refresh: () => void
}

/**
 * Relay-first timeline.
 *
 * Subscribes to exactly the relays it is given (one relay, a relay set or the
 * user's relays), de-duplicates events arriving from several relays and
 * paginates with an `until` cursor. The subscription is closed whenever the
 * relay set, the filter or the component changes.
 */
export function useTimeline(options: TimelineOptions): TimelineResult {
  const { pool } = useNostr()
  const { settings } = useSettings()
  const relays = useMemo(() => uniqueRelayUrls(options.relays), [options.relays.join(",")])
  const enabled = options.enabled !== false && relays.length > 0
  const limit = options.limit ?? 40
  const search = options.search?.trim() || undefined

  const [events, setEvents] = useState<NostrEvent[]>([])
  const [loading, setLoading] = useState(enabled)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [eose, setEose] = useState(false)
  const [closeReasons, setCloseReasons] = useState<string[]>([])
  const eventsRef = useRef<NostrEvent[]>([])
  const subscriptionRef = useRef<{ close: () => void } | undefined>(undefined)
  const flushTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const pending = useRef<NostrEvent[]>([])

  const authorsKey = (options.authors ?? []).join(",")
  const kindsKey = (options.kinds ?? []).join(",")
  const postTypesKey = (options.postTypes ?? []).join(",")
  const since = options.since
  const untilBound = options.until
  const queryKey = `${relays.join("|")}#${authorsKey}#${kindsKey}#${postTypesKey}#${search ?? ""}#${limit}#${since ?? ""}#${untilBound ?? ""}`

  const updateEvents = useCallback((incoming: NostrEvent[], replace: boolean) => {
    const next = sortEvents(dedupeEvents(replace ? incoming : [...eventsRef.current, ...incoming]))
    eventsRef.current = next
    setEvents(next)
  }, [])

  const flush = useCallback(() => {
    if (flushTimer.current) {
      clearTimeout(flushTimer.current)
      flushTimer.current = undefined
    }
    if (pending.current.length === 0) return
    const batch = pending.current
    pending.current = []
    updateEvents(batch, false)
  }, [updateEvents])

  const start = useCallback(
    (mode: "reset" | "more") => {
      subscriptionRef.current?.close()
      if (!enabled) {
        setLoading(false)
        setLoadingMore(false)
        return
      }

      const cursor = mode === "more" ? nextUntil(eventsRef.current) : undefined
      if (mode === "more" && cursor === undefined) {
        setHasMore(false)
        return
      }
      // A custom range end caps the cursor; pagination keeps walking backwards
      // from it, so no post is skipped while filters are active.
      const until =
        cursor !== undefined && options.until !== undefined
          ? Math.min(cursor, options.until)
          : (cursor ?? options.until)

      const filters = buildFeedFilters({
        relayUrls: relays,
        authors: options.authors,
        limit,
        until,
        since: options.since,
        search,
      })
      // Narrow the subscription to the selected post types so pagination keeps
      // returning useful events instead of pages the UI would discard.
      const requestedKinds = options.kinds && options.kinds.length > 0 ? options.kinds : undefined
      const typeKinds = options.postTypes && options.postTypes.length > 0
        ? kindsForPostTypes(options.postTypes)
        : undefined
      const effectiveKinds = requestedKinds && typeKinds
        ? requestedKinds.filter((kind) => typeKinds.includes(kind))
        : (requestedKinds ?? typeKinds)
      if (effectiveKinds && effectiveKinds.length > 0) {
        for (const filter of filters) filter.kinds = effectiveKinds
      }

      if (mode === "reset") {
        pending.current = []
        eventsRef.current = []
        setEvents([])
        setEose(false)
        setHasMore(true)
        setCloseReasons([])
        setLoading(true)
      } else {
        setLoadingMore(true)
      }

      let received = 0
      const reasons: string[] = []

      subscriptionRef.current = pool.subscribe(filters, {
        relays,
        onEvent: (event) => {
          received += 1
          pending.current.push(event)
          if (!flushTimer.current) flushTimer.current = setTimeout(flush, 150)
        },
        onEose: () => {
          flush()
          setLoading(false)
          setLoadingMore(false)
          setEose(true)
          if (received === 0) setHasMore(false)
          else if (received < limit * 0.25) setHasMore(false)
          if (reasons.length > 0) setCloseReasons([...new Set(reasons)])
        },
        onClosed: (message, relay) => {
          reasons.push(`${relay}: ${message}`)
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pool, enabled, queryKey, limit, search, flush],
  )

  useEffect(() => {
    start("reset")
    return () => {
      subscriptionRef.current?.close()
      subscriptionRef.current = undefined
      if (flushTimer.current) clearTimeout(flushTimer.current)
      flushTimer.current = undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, enabled, pool])

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return
    start("more")
  }, [loading, loadingMore, hasMore, start])

  const refresh = useCallback(() => start("reset"), [start])

  // A "replies only" selection must include replies even when the user setting
  // hides them from feeds; every other selection follows the setting.
  const repliesOnly = Boolean(
    options.postTypes && options.postTypes.length === 1 && options.postTypes[0] === "reply",
  )
  const includeReplies = repliesOnly ? true : (options.includeReplies ?? settings.includeReplies)
  // Muted words are applied by the unified filter engine (`filterPosts`), which
  // owns that decision now so pages can offer a "muted words" toggle.
  const visibleEvents = useMemo(
    () =>
      filterFeed(events, {
        includeReplies,
        postTypes: options.postTypes,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, includeReplies, postTypesKey],
  )

  return {
    events: visibleEvents,
    loading,
    loadingMore,
    hasMore,
    eose,
    closeReasons,
    loadMore,
    refresh,
  }
}
